import * as React from "react";
import { useEffect, useRef } from "react";
import classes from "./BankDetails.module.scss";
import { clsx } from "clsx";
import { Times } from "@/components/ui/Icon/Times";

type BankDetailsDirectDebitGuaranteeModalProps = {
  /**
   * Additional className
   */
  className?: string;
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * Callback function to close the modal
   */
  onClose: () => void;
  /**
   * Modal content
   */
  children?: React.ReactNode;
};

export function BankDetailsDirectDebitGuaranteeModal(
  props: BankDetailsDirectDebitGuaranteeModalProps,
) {
  const { className, open, onClose, children } = props;
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }

    if (open) {
      dialogRef.current.showModal();
    } else {
      dialogRef.current.close();
    }
  }, [open]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog
      className={clsx(classes.modal, className)}
      onClose={onClose}
      onClick={handleBackdropClick}
      aria-label="Direct Debit Guarantee"
      ref={dialogRef}
    >
      <div className={classes.modal__padding}>
        <div className={classes.modal__header}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="113"
            fill="none"
            viewBox="0 0 350 113"
            className={classes.modal__logo}
          >
            <path
              fill="#000"
              fillRule="evenodd"
              d="M164.652 23.724h18.153s17.973-.361 17.792 15.935c-.18 12.135-17.426 13.944-17.426 13.944h-18.517zm54.306 58.497c.51 2.37 2.229 4.983 7.962 5.25 7.623.18 11.981-2.719 11.981-2.719v5.614s-8.35 1.995-12.345 1.995c-4.175 0-16.52-.908-16.52-12.315 0-9.234 10.167-12.133 15.974-12.133 10.167.362 15.068 3.985 14.886 14.306 0 .149-16.726.038-21.938.002m.024-4.532h13.928s0-5.25-6.717-5.25c-5.052 0-6.705 2.718-7.211 5.25m44.062-5.67c2.015-2.18 5.399-3.927 11.074-3.927 11.256 0 13.072 9.417 12.891 11.952s-1.089 12.132-14.157 12.132c-4.937 0-7.958-1.68-9.806-3.767v3.042h-8.896V58.854h8.896V72.02zm7.627 15.087c5.264-.183 7.623-4.706 7.623-6.88s-1.272-6.7-7.989-6.7c-5.212 0-6.799 3.927-7.261 5.846v2.362c.458 1.957 2.053 5.372 7.627 5.372m76.79-.904.179 5.072s-4.174.904-6.896.904c-2.723 0-9.807-1.629-9.807-8.513V73.162h-7.44v-4.706h7.44V62.84h9.08v5.615h7.444v4.706h-7.444v9.057s0 4.528 3.632 4.528c3.629 0 3.812-.545 3.812-.545m-27.594-57.588h10.529v25.17h8.892v-25.17H350V23.36h-30.133zm-3.633 1.267 1.09-5.253s-4.539-1.99-12.525-1.99c-7.806 0-20.331 4.346-20.331 16.117 0 11.59 13.251 15.574 21.421 15.574s10.891-1.268 10.891-1.268l-.906-5.795s-3.633 1.087-9.806 1.087c-5.991-.181-12.158-4.89-12.158-10.14.179-4.708 5.624-9.961 12.707-9.961 5.443 0 9.617 1.629 9.617 1.629m-80.239-6.337s14.887-.724 14.887 7.968c0 7.426-8.53 7.968-8.53 7.968s2.358.362 3.449 1.991c.907 1.448 6.897 12.315 6.897 12.315h-9.079s-1.452-1.812-2.179-3.077c-.544-1.268-2.179-3.985-2.179-3.985s-1.268-4.528-5.628-4.528c-.109 0-1.882-.067-3.268-.119v11.71h-8.712V23.543zm-5.628 13.582h4.722s6.534-.363 6.897-4.346c.183-3.803-6.351-4.347-6.351-4.347h-5.268zm36.488 4.709h14.524V36.22h-14.524V29.34h15.797v-5.795h-24.687v30.242h24.87v-5.433h-15.978v-6.518zm35.399 23.178h8.896v-6.159h-8.896zm0 26.44h8.896V68.456h-8.896zm-96.936-37.667h8.713V23.544h-8.713zm-40.666 7.607h18.153s17.973-.365 17.792 15.935c-.18 12.132-17.426 13.946-17.426 13.946h-18.517zm8.896 24.267h5.809s11.981-.725 11.981-9.961-12.708-10.142-12.708-10.142h-5.082zm0-37.669h5.809s11.981-.906 11.981-9.96c0-9.417-12.708-10.14-12.708-10.14h-5.082zm-90.405-6.264v60.593s-35.22-6.34-35.22-30.423c.002-19.21 26.073-27.676 35.22-30.17m0-1.13c-21.168-1.528-69.165 8.37-69.165 30.03 0 14.123 25.416 34.77 66.805 35.312 1.633 0 48.65-1.811 47.925-52.155-.61-42.283-32.334-49.69-42.479-50.979V.228c40.444 2.561 72.252 26.824 72.252 56.272 0 31.149-35.582 56.5-79.331 56.5S0 87.647 0 56.5 35.399 0 79.15 0q2.01 0 3.995.072z"
              clipRule="evenodd"
            ></path>
          </svg>
          <button
            className={classes.modal__close}
            onClick={onClose}
            aria-label="Close"
          >
            <Times color="currentColor" size={30} strokeWidth={2} />
          </button>
        </div>
        <div className={classes.modal__content}>{children}</div>
      </div>
    </dialog>
  );
}
